/**
 * Client side of the EMT assistant (flag `emt_assistant`): the callable wrapper, what the current page
 * tells the helper (the job being viewed and what is missing for it), and the "Back to what you were
 * doing" return point. No React here; the panel is src/components/assistant/EmtAssistant.tsx.
 */
import type { AssistantResponse } from './emtAssistant';

export async function askAssistant(question: string, jobId: string | null): Promise<AssistantResponse> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { app } = await import('../firebase');
  if (!app) return { available: false, reason: 'analysis_error' };
  const fn = httpsCallable<{ question: string; jobId?: string }, AssistantResponse>(getFunctions(app), 'emtAssistantAsk');
  const result = await fn(jobId ? { question, jobId } : { question });
  return result.data ?? { available: false, reason: 'analysis_error' };
}

/** The server's own message for a refusal we can explain (daily limit, too long), else null. */
export function explainAssistantError(err: unknown): string | null {
  const code = (err as { code?: string } | null)?.code ?? '';
  const message = (err as { message?: string } | null)?.message ?? '';
  if (code.endsWith('resource-exhausted') || code.endsWith('invalid-argument')) return message || null;
  return null;
}

/* ------------------------------------------------------------------ page context */

export type AssistantJobContext = {
  jobId: string;
  company: string;
  title: string;
  /** Required items NOT on the profile at all ("how to get it" tip). */
  missing: string[];
  /** Required items on the profile whose document is not reviewed yet (a different tip; never "how to get it"). */
  pending: string[];
  /**
   * Thumbs up but a score under 75: "Your score is lower because of ..." (the job's "Why?" breakdown,
   * src/lib/readiness/jobScoreBreakdown.ts), and where the biggest fix is. Null otherwise.
   */
  lower?: { text: string; href: string | null } | null;
};

let jobContext: AssistantJobContext | null = null;
const listeners = new Set<() => void>();

/** The job page sets this while it is open (and clears it on leave). */
export function setAssistantJobContext(ctx: AssistantJobContext | null): void {
  const same = JSON.stringify(ctx) === JSON.stringify(jobContext);
  jobContext = ctx;
  if (!same) listeners.forEach((l) => l());
}
export function getAssistantJobContext(): AssistantJobContext | null {
  return jobContext;
}
export function subscribeAssistantJobContext(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/* ------------------------------------------------------------------ return point */

const RETURN_KEY = 'rr-assistant-return';
export type ReturnPoint = { href: string; label: string };

export function saveReturnPoint(p: ReturnPoint): void {
  try {
    window.sessionStorage.setItem(RETURN_KEY, JSON.stringify(p));
  } catch {
    /* storage blocked: no back link, nothing else changes */
  }
}
export function readReturnPoint(): ReturnPoint | null {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(RETURN_KEY) || 'null') as ReturnPoint | null;
    return v && typeof v.href === 'string' && v.href.startsWith('/') && !v.href.startsWith('//') ? v : null;
  } catch {
    return null;
  }
}
export function clearReturnPoint(): void {
  try {
    window.sessionStorage.removeItem(RETURN_KEY);
  } catch {
    /* ignore */
  }
}
